import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { useListingPaths } from '../lib/listingPaths';
import { apiRequest, ApiError } from '../lib/api';
import { downscaleWithThumb, fileSize } from '../lib/downscale';
import { ListingTourEditor, type ListingVideo, type TourCoverage } from '../components/ListingTourEditor';
import { AuthedImage } from '../components/AuthedImage';
import { money, humanise, shortDate } from '../lib/format';
import { COUNTIES_BY_USE } from '../lib/counties';
import {
  Alert, Button, Card, ErrorBlock, Field, LoadingBlock, Select, StatusPill, TextInput,
} from '../components/ui';

/**
 * The listing form — create from a vacant unit, or manage an existing advert.
 *
 * One component for both because they are the same form with different buttons;
 * splitting them would mean maintaining two copies of the amenity list, the
 * photo handling and the validation.
 *
 * Everything a listing can inherit from the unit is fetched from the server
 * rather than assembled here, so the API and the form cannot disagree about
 * what "pre-populated" means.
 */

const MAX_PHOTOS = 5;

interface Amenity { code: string; label: string }
interface Photo { id: string; position: number; mime_type: string; size_bytes: number }

interface Listing {
  id: string; status: string; title: string; description: string | null;
  unit_type: string | null; bedrooms: number | null; bathrooms: number | null;
  county: string | null; estate_area: string | null;
  rent: string; deposit: string | null; available_from: string | null;
  amenities: string[]; contact_preference: string;
  contact_name: string | null; contact_phone: string | null; contact_email: string | null;
  /*
   * NULL on an external advert (133): there is no unit on the books behind it
   * and never will be. Declared nullable so the form cannot print the literal
   * "null · Unit null" and think it has rendered a subtitle.
   */
  unit_id: string | null;
  property_name: string | null; unit_number: string | null;
  rejection_reason: string | null;
  /*
   * Computed by the server (visibility() in domain/listings.ts), never derived
   * here. `status` alone cannot answer "is this on Find Your Next Home": an
   * external advert can read published and be invisible, because the public
   * read policy also wants a live paid window.
   */
  on_site: boolean; needs_allowance: boolean; paid_until: string | null;
  published_at: string | null; is_sold_out: boolean;
  service_charge: string | null; utilities_note: string | null;
  expires_at: string | null; availability_confirmed_at: string | null;
  stay_type?: string; nightly_rate?: string | null; weekly_rate?: string | null;
  cleaning_fee?: string | null; min_nights?: number | null; max_guests?: number | null;
  check_in_time?: string | null; check_out_time?: string | null; house_rules?: string | null;
  booking_url?: string | null; tourism_licence?: string | null;
}

interface Enquiry {
  id: string; name: string; phone: string | null; email: string | null;
  message: string | null; status: string; created_at: string;
  wants_viewing: boolean; preferred_viewing_date: string | null;
  stay_from?: string | null; stay_to?: string | null; guests?: number | null;
  appointment_id: string | null; viewing_starts_at: string | null;
}

type Form = {
  title: string; description: string; unitType: string;
  bedrooms: string; bathrooms: string; county: string; estateArea: string;
  rent: string; deposit: string; availableFrom: string;
  amenities: string[]; contactPreference: string;
  contactName: string; contactPhone: string; contactEmail: string;
  serviceCharge: string; utilitiesNote: string;
  // Short stays (155).
  stayType: string; weeklyRate: string; cleaningFee: string; minNights: string; maxGuests: string;
  checkInTime: string; checkOutTime: string; houseRules: string; bookingUrl: string; tourismLicence: string;
};

const EMPTY: Form = {
  title: '', description: '', unitType: '', bedrooms: '', bathrooms: '',
  county: '', estateArea: '', rent: '', deposit: '', availableFrom: '',
  amenities: [], contactPreference: 'phone',
  contactName: '', contactPhone: '', contactEmail: '',
  serviceCharge: '', utilitiesNote: '',
  stayType: 'long_term', weeklyRate: '', cleaningFee: '', minNights: '', maxGuests: '',
  checkInTime: '', checkOutTime: '', houseRules: '', bookingUrl: '', tourismLicence: '',
};

/** Only a draft or a rejected listing may be edited; the rest are frozen until
 *  withdrawn. Mirrors the server, which is what actually enforces it. */
const EDITABLE = ['draft', 'rejected'];
/*
 * Pictures and the walkthrough stay editable on a live advert. Taking a home
 * off the site to add a kitchen photo or a 360 view, and then waiting for review
 * to get it back, is the reason landlords stopped adding them. The wording,
 * price and terms still need a withdrawal, because those are what review is for.
 */
const MEDIA_EDITABLE = ['draft', 'rejected', 'pending_review', 'published', 'reserved'];

export function ListingForm() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const unitId = search.get('unitId');
  /*
   * Advertising a home that is not on the books (133). No unit, so no vacancy
   * check, no defaults to prefill from, and the advert has to be paid for
   * before the public can see it — which the seller's own list says plainly
   * once it is approved.
   */
  const paths = useListingPaths();
  // Somebody from the public has no units: every advert of theirs is external.
  const external = search.get('external') === '1' || paths.publicPoster;
  const navigate = useNavigate();
  const isNew = !id;

  const catalogue = useApi<{
    amenities: Amenity[]; maxPhotos: number; maxVideoBytes: number;
  }>('/api/v1/listings/amenities');
  const defaults = useApi<{ defaults: any }>(isNew && unitId ? `/api/v1/listings/defaults/${unitId}` : null);
  const existing = useApi<{
    listing: Listing; photos: Photo[]; enquiries: Enquiry[];
    tour: TourCoverage; video: ListingVideo | null;
  }>(
    id ? `/api/v1/listings/${id}` : null);

  const [form, setForm] = useState<Form>(EMPTY);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Seed from whichever source applies. Runs once per load of that source.
  useEffect(() => {
    const d = defaults.data?.defaults;
    if (!d) return;
    setForm(f => ({
      ...f,
      title: d.title ?? '', description: d.description ?? '',
      unitType: d.unitType ?? '', bedrooms: d.bedrooms?.toString() ?? '',
      bathrooms: d.bathrooms?.toString() ?? '', county: d.county ?? '',
      estateArea: d.estateArea ?? '', rent: d.rent ?? '', deposit: d.deposit ?? '',
      contactName: d.contactName ?? '', contactPhone: d.contactPhone ?? '',
      contactEmail: d.contactEmail ?? '',
    }));
  }, [defaults.data]);

  useEffect(() => {
    const l = existing.data?.listing;
    if (!l) return;
    setForm({
      title: l.title, description: l.description ?? '', unitType: l.unit_type ?? '',
      bedrooms: l.bedrooms?.toString() ?? '', bathrooms: l.bathrooms?.toString() ?? '',
      county: l.county ?? '', estateArea: l.estate_area ?? '',
      rent: l.rent, deposit: l.deposit ?? '', availableFrom: l.available_from ?? '',
      amenities: l.amenities ?? [], contactPreference: l.contact_preference,
      contactName: l.contact_name ?? '', contactPhone: l.contact_phone ?? '',
      contactEmail: l.contact_email ?? '',
      serviceCharge: l.service_charge ?? '', utilitiesNote: l.utilities_note ?? '',
      stayType: l.stay_type ?? 'long_term', weeklyRate: l.weekly_rate ?? '', cleaningFee: l.cleaning_fee ?? '',
      minNights: l.min_nights?.toString() ?? '', maxGuests: l.max_guests?.toString() ?? '',
      checkInTime: l.check_in_time ?? '', checkOutTime: l.check_out_time ?? '',
      houseRules: l.house_rules ?? '', bookingUrl: l.booking_url ?? '', tourismLicence: l.tourism_licence ?? '',
    });
    setPhotos(existing.data!.photos);
  }, [existing.data]);

  const listing = existing.data?.listing;
  const editable = isNew || (listing ? EDITABLE.includes(listing.status) : false);
  const mediaEditable = !isNew && !!listing && MEDIA_EDITABLE.includes(listing.status);

  const short = form.stayType === 'short_stay';
  const body = () => ({
    ...(isNew && unitId ? { unitId } : {}),
    title: form.title,
    description: form.description || undefined,
    unitType: form.unitType || undefined,
    bedrooms: form.bedrooms === '' ? undefined : Number(form.bedrooms),
    bathrooms: form.bathrooms === '' ? undefined : Number(form.bathrooms),
    county: form.county || undefined,
    estateArea: form.estateArea || undefined,
    rent: form.rent,
    deposit: form.deposit || undefined,
    availableFrom: form.availableFrom || undefined,
    amenities: form.amenities,
    contactPreference: form.contactPreference,
    contactName: form.contactName || undefined,
    contactPhone: form.contactPhone || undefined,
    contactEmail: form.contactEmail || undefined,
    serviceCharge: short ? undefined : form.serviceCharge || undefined,
    utilitiesNote: form.utilitiesNote || undefined,
    stayType: form.stayType,
    ...(short ? {
      nightlyRate: form.rent,
      weeklyRate: form.weeklyRate || undefined,
      cleaningFee: form.cleaningFee || undefined,
      minNights: form.minNights ? Number(form.minNights) : undefined,
      maxGuests: form.maxGuests ? Number(form.maxGuests) : undefined,
      checkInTime: form.checkInTime || undefined,
      checkOutTime: form.checkOutTime || undefined,
      houseRules: form.houseRules || undefined,
      // '' clears a link that was there.
      bookingUrl: form.bookingUrl.trim(),
      tourismLicence: form.tourismLicence || undefined,
    } : {}),
  });

  const save = async (): Promise<string | null> => {
    if (isNew) {
      const r = await apiRequest<{ listing: Listing }>('/api/v1/listings',
        { method: 'POST', body: body() });
      return r.listing.id;
    }
    await apiRequest(`/api/v1/listings/${id}`, { method: 'PATCH', body: body() });
    return id!;
  };

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label); setError(null); setNote(null);
    try { await fn(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'That did not save. Please try again.'); }
    finally { setBusy(null); }
  };

  const onSaveDraft = () => run('draft', async () => {
    const newId = await save();
    if (isNew) navigate(paths.listing(newId!), { replace: true });
    else { existing.reload(); setNote('Saved.'); }
  });

  const onPublish = () => run('publish', async () => {
    const newId = await save();
    await apiRequest(`/api/v1/listings/${newId}/submit`, { method: 'POST' });
    navigate(paths.home);
  });

  const onWithdraw = () => run('withdraw', async () => {
    await apiRequest(`/api/v1/listings/${id}/withdraw`, { method: 'POST' });
    existing.reload();
    setNote('Withdrawn. It is a draft again and no longer public.');
  });

  const onState = (status: 'reserved' | 'rented') => run(status, async () => {
    await apiRequest(`/api/v1/listings/${id}/state`, { method: 'POST', body: { status } });
    existing.reload();
  });

  // "It is still available" — renews the listing's window on the public site.
  const onStillAvailable = () => run('fresh', async () => {
    const r = await apiRequest<{ expiresAt: string }>(`/api/v1/listings/${id}/confirm-available`, { method: 'POST' });
    existing.reload();
    setNote(`Thanks. It stays on Find Your Next Home until ${shortDate(r.expiresAt)}.`);
  });

  // Book the viewing a house hunter asked for, into the calendar.
  const [viewingAt, setViewingAt] = useState<Record<string, string>>({});
  const onBookViewing = (enquiryId: string) => run(`view-${enquiryId}`, async () => {
    await apiRequest(`/api/v1/enquiries/${enquiryId}/viewing`, {
      method: 'POST', body: { startsAt: viewingAt[enquiryId] },
    });
    existing.reload();
    setNote('Viewing booked. It is in your calendar, and the enquiry is marked as contacted.');
  });

  const onSoldOut = (soldOut: boolean) => run('soldout', async () => {
    await apiRequest(`/api/v1/listings/${id}/sold-out`, { method: 'POST', body: { soldOut } });
    existing.reload();
    setNote(soldOut
      ? 'Marked as taken. The advert stays up with a banner and stops taking enquiries.'
      : 'Back to available. The advert is accepting enquiries again.');
  });

  /**
   * Photos are shrunk on this device before they are sent — see lib/downscale.
   * A phone photo is 4-5MB; what leaves here is nearer 200KB, which matters
   * both for the landlord's bundle and for whoever loads the page later.
   */
  const onFiles = async (files: FileList | null) => {
    if (!files?.length || !id) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) { setError(`A listing may have at most ${MAX_PHOTOS} photos.`); return; }

    setBusy('photos'); setError(null); setNote(null);
    try {
      let added = 0;
      for (const file of Array.from(files).slice(0, room)) {
        // The 320px thumbnail comes from the same decode (089): it is what
        // Explore's cards and the phone's welcome strip download instead.
        const small = await downscaleWithThumb(file);
        await apiRequest(`/api/v1/listings/${id}/photos`, {
          method: 'POST',
          body: { mimeType: small.mimeType, contentBase64: small.contentBase64, thumbBase64: small.thumbBase64 },
        });
        added++;
        setNote(`${file.name} — ${fileSize(file.size)} shrunk to ${fileSize(small.bytes)}`);
      }
      if (files.length > room) {
        setError(`Only ${added} added — a listing may have at most ${MAX_PHOTOS} photos.`);
      }
      existing.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That photo could not be added.');
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const removePhoto = (photoId: string) => run('photos', async () => {
    await apiRequest(`/api/v1/listings/${id}/photos/${photoId}`, { method: 'DELETE' });
    existing.reload();
  });

  const toggleAmenity = (code: string) => setForm(f => ({
    ...f,
    amenities: f.amenities.includes(code)
      ? f.amenities.filter(a => a !== code)
      : [...f.amenities, code],
  }));

  if (existing.loading || defaults.loading || catalogue.loading) {
    return <div className="page"><LoadingBlock /></div>;
  }
  if (existing.error) {
    return <div className="page"><ErrorBlock error={existing.error} onRetry={existing.reload} /></div>;
  }
  if (isNew && !unitId && !external) {
    return (
      <div className="page">
        <Alert tone="warning">
          Choose a vacant unit from Vacancies to advertise it, or{' '}
          <Link to={paths.newExternal}>advertise a home you do not manage here</Link>.
        </Alert>
      </div>
    );
  }

  const amenities = catalogue.data?.amenities ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">
            {!isNew ? 'Manage listing' : external ? 'Advertise a home' : 'List a unit'}
          </h1>
          <p className="page-sub">
            {/* An external advert has no unit to name, and the thing its owner
                most needs to know is that approval alone will not put it up. */}
            {isNew && external
              ? 'A home you do not manage here. It goes up once it has been reviewed and you have spent a place from your advertising allowance.'
              /*
               * An EXISTING external advert has no property and no unit, so the
               * managed sub-line rendered the literal text "null · Unit null".
               * Checked on the row rather than on the `external` query
               * parameter, because that parameter is only present while the
               * advert is being created.
               */
              : listing && listing.unit_id === null
                ? 'A home you do not manage here.'
                : listing
                  ? `${listing.property_name} · Unit ${listing.unit_number}`
                  : defaults.data
                    ? `${defaults.data.defaults.propertyName} · Unit ${defaults.data.defaults.unitNumber}`
                    : ''}
          </p>
        </div>
        {listing && (
          /*
           * The pill used to come straight from `status`, so it read a
           * success-toned "Published" for an advert on no page. It now says
           * what is true, and the banner below says it in words — a heading is
           * read before a colour.
           */
          <StatusPill
            status={listing.on_site ? 'On the site' : humanise(listing.status)}
            tone={listing.on_site ? 'success'
              : listing.needs_allowance ? 'warning'
                : listing.status === 'rejected' ? 'danger' : 'neutral'}
          />
        )}
      </div>

      {error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></div>}
      {note && <div style={{ marginBottom: 16 }}><Alert tone="success">{note}</Alert></div>}

      {listing?.needs_allowance && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="warning">
            <strong>Approved, but not on the site yet.</strong> This is a home you do
            not manage on NyumbaSuite, so it needs a place from your advertising
            allowance before anybody can find it.{' '}
            <Link to={paths.bundles}>Put it up</Link>.
          </Alert>
        </div>
      )}
      {listing?.status === 'rejected' && listing.rejection_reason && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="warning">
            <strong>Not published:</strong> {listing.rejection_reason}
            {' '}Make the change and submit it again.
          </Alert>
        </div>
      )}
      {listing?.status === 'pending_review' && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="info">
            With our team for review. It goes live once approved — usually within a day.
          </Alert>
        </div>
      )}
      {!editable && listing && !['pending_review', 'rejected'].includes(listing.status) && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="info">
            This listing is {humanise(listing.status).toLowerCase()}. Withdraw it to change the wording,
            price or terms. Photos, the 360 tour and the video can be changed while it stays live.
          </Alert>
        </div>
      )}

      <div className="stack">
        <Card title="The advert">
          <Field label="Listing title" htmlFor="l-title"
            hint="What a tenant sees first. Say the size, the type and the area.">
            <TextInput id="l-title" value={form.title} disabled={!editable} required
              onChange={e => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Description" htmlFor="l-desc">
            <textarea id="l-desc" className="input" rows={5} disabled={!editable}
              placeholder="What makes this home worth a viewing?"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })} />
          </Field>
        </Card>

        <Card title="The home">
          <div className="grid grid-3">
            <Field label="Property type" htmlFor="l-type">
              <TextInput id="l-type" value={form.unitType} disabled={!editable}
                onChange={e => setForm({ ...form, unitType: e.target.value })} />
            </Field>
            <Field label="Bedrooms" htmlFor="l-bed">
              <TextInput id="l-bed" type="number" min="0" value={form.bedrooms} disabled={!editable}
                onChange={e => setForm({ ...form, bedrooms: e.target.value })} />
            </Field>
            <Field label="Bathrooms" htmlFor="l-bath">
              <TextInput id="l-bath" type="number" min="0" value={form.bathrooms} disabled={!editable}
                onChange={e => setForm({ ...form, bathrooms: e.target.value })} />
            </Field>
          </div>
          <div className="grid grid-2">
            <Field label="County" htmlFor="l-county">
              {/* The list, because Find Your Next Home filters on an exact
                  match: a listing typed as "Nrb" is a listing nobody in
                  Nairobi can find. */}
              <Select id="l-county" value={form.county} disabled={!editable}
                onChange={e => setForm({ ...form, county: e.target.value })}>
                <option value="">Choose a county…</option>
                {COUNTIES_BY_USE.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Estate / area" htmlFor="l-area">
              <TextInput id="l-area" value={form.estateArea} disabled={!editable}
                onChange={e => setForm({ ...form, estateArea: e.target.value })} />
            </Field>
          </div>
          <Field label="Kind of let" htmlFor="l-stay"
            hint={short ? 'Furnished, booked by the night. Shown under Short stays on Find your next home.'
              : 'A monthly tenancy. Shown under Long-term rentals.'}>
            <Select id="l-stay" value={form.stayType} disabled={!editable}
              onChange={e => setForm({ ...form, stayType: e.target.value })}>
              <option value="long_term">Long-term rental (monthly)</option>
              <option value="short_stay">Short stay (nightly, Airbnb style)</option>
            </Select>
          </Field>
          {short && (
            <div className="grid grid-3">
              <Field label="Price per night (KES)" htmlFor="l-night">
                <TextInput id="l-night" type="number" min="0" value={form.rent} disabled={!editable} required
                  onChange={e => setForm({ ...form, rent: e.target.value })} />
              </Field>
              <Field label="Weekly rate (KES)" htmlFor="l-week" hint="Optional.">
                <TextInput id="l-week" type="number" min="0" value={form.weeklyRate} disabled={!editable}
                  onChange={e => setForm({ ...form, weeklyRate: e.target.value })} />
              </Field>
              <Field label="Cleaning fee (KES)" htmlFor="l-clean" hint="Per stay. Optional.">
                <TextInput id="l-clean" type="number" min="0" value={form.cleaningFee} disabled={!editable}
                  onChange={e => setForm({ ...form, cleaningFee: e.target.value })} />
              </Field>
              <Field label="Minimum nights" htmlFor="l-min">
                <TextInput id="l-min" type="number" min="1" value={form.minNights} disabled={!editable}
                  onChange={e => setForm({ ...form, minNights: e.target.value })} />
              </Field>
              <Field label="Sleeps (guests)" htmlFor="l-guests">
                <TextInput id="l-guests" type="number" min="1" value={form.maxGuests} disabled={!editable}
                  onChange={e => setForm({ ...form, maxGuests: e.target.value })} />
              </Field>
              <Field label="Check-in / check-out" htmlFor="l-cin">
                <div className="row" style={{ gap: 6 }}>
                  <TextInput id="l-cin" placeholder="From 2pm" value={form.checkInTime} disabled={!editable}
                    onChange={e => setForm({ ...form, checkInTime: e.target.value })} />
                  <TextInput aria-label="Check-out" placeholder="By 10am" value={form.checkOutTime} disabled={!editable}
                    onChange={e => setForm({ ...form, checkOutTime: e.target.value })} />
                </div>
              </Field>
              <Field label="Your Airbnb or Booking.com link" htmlFor="l-book"
                hint="Optional. Adds a Book button that opens your own listing there.">
                <TextInput id="l-book" type="url" placeholder="https://www.airbnb.com/rooms/..." value={form.bookingUrl}
                  disabled={!editable} onChange={e => setForm({ ...form, bookingUrl: e.target.value })} />
              </Field>
              <Field label="Tourism licence no." htmlFor="l-lic" hint="Optional. Shown on the listing when given.">
                <TextInput id="l-lic" value={form.tourismLicence} disabled={!editable}
                  onChange={e => setForm({ ...form, tourismLicence: e.target.value })} />
              </Field>
            </div>
          )}
          {short && (
            <Field label="House rules" htmlFor="l-rules" hint="No smoking, quiet hours, parties, pets.">
              <textarea id="l-rules" className="input" rows={3} maxLength={2000} value={form.houseRules}
                disabled={!editable} onChange={e => setForm({ ...form, houseRules: e.target.value })} />
            </Field>
          )}
          {!short && (
          <div className="grid grid-3">
            <Field label="Monthly rent (KES)" htmlFor="l-rent">
              <TextInput id="l-rent" type="number" min="0" value={form.rent} disabled={!editable} required
                onChange={e => setForm({ ...form, rent: e.target.value })} />
            </Field>
            <Field label="Deposit (KES)" htmlFor="l-dep">
              <TextInput id="l-dep" type="number" min="0" value={form.deposit} disabled={!editable}
                onChange={e => setForm({ ...form, deposit: e.target.value })} />
            </Field>
            <Field label="Service charge (KES / month)" htmlFor="l-svc"
              hint="On top of rent. Leave empty if there is none.">
              <TextInput id="l-svc" type="number" min="0" value={form.serviceCharge} disabled={!editable}
                onChange={e => setForm({ ...form, serviceCharge: e.target.value })} />
            </Field>
            <Field label="Utilities" htmlFor="l-util"
              hint="How water, electricity and garbage are paid, e.g. “Water by meter, KPLC tokens”.">
              <TextInput id="l-util" maxLength={300} value={form.utilitiesNote} disabled={!editable}
                onChange={e => setForm({ ...form, utilitiesNote: e.target.value })} />
            </Field>
            <Field label="Available from" htmlFor="l-avail">
              <TextInput id="l-avail" type="date" value={form.availableFrom} disabled={!editable}
                onChange={e => setForm({ ...form, availableFrom: e.target.value })} />
            </Field>
          </div>
          )}
        </Card>

        <Card title="Amenities">
          <div className="lf-chips">
            {amenities.map(a => (
              <button key={a.code} type="button" disabled={!editable}
                className={`lf-chip${form.amenities.includes(a.code) ? ' on' : ''}`}
                aria-pressed={form.amenities.includes(a.code)}
                onClick={() => toggleAmenity(a.code)}>
                {a.label}
              </button>
            ))}
          </div>
        </Card>

        <Card title="Photos">
          {isNew ? (
            <p className="page-sub">Save the listing first, then add photos.</p>
          ) : (
            <>
              <div className="lf-photos">
                {photos.map(p => (
                  <div className="lf-photo" key={p.id}>
                    {/* Not a bare <img>: this route needs a Bearer token,
                        which an img tag cannot send — see AuthedImage. */}
                    <AuthedImage src={`/api/v1/listings/photos/${p.id}/content`} alt="" />
                    {mediaEditable && (
                      <button type="button" className="lf-photo-x" aria-label="Remove photo"
                        onClick={() => removePhoto(p.id)}>×</button>
                    )}
                    <span className="lf-photo-size">{fileSize(p.size_bytes)}</span>
                  </div>
                ))}
                {mediaEditable && photos.length < MAX_PHOTOS && (
                  <button type="button" className="lf-photo-add"
                    onClick={() => fileInput.current?.click()} disabled={busy === 'photos'}>
                    <span aria-hidden>+</span>
                    <span>{busy === 'photos' ? 'Adding…' : 'Add photo'}</span>
                  </button>
                )}
              </div>
              <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp"
                multiple hidden onChange={e => onFiles(e.target.files)} />
              <p className="page-sub" style={{ marginTop: 8 }}>
                Up to {MAX_PHOTOS} photos. They are resized on your device before uploading,
                so a large photo from your phone is fine.
              </p>
            </>
          )}
        </Card>

        {!isNew && id && existing.data?.tour && (
          <ListingTourEditor
            listingId={id}
            editable={mediaEditable}
            tour={existing.data.tour}
            video={existing.data.video}
            maxVideoBytes={catalogue.data?.maxVideoBytes ?? 80 * 1024 * 1024}
            onChange={() => existing.reload()}
          />
        )}

        <Card title="How tenants reach you">
          <Field label="Show tenants" htmlFor="l-pref"
            hint="Only what you choose here appears on the public page.">
            <Select id="l-pref" value={form.contactPreference} disabled={!editable}
              onChange={e => setForm({ ...form, contactPreference: e.target.value })}>
              <option value="phone">My phone number</option>
              <option value="email">My email address</option>
              <option value="both">Both</option>
            </Select>
          </Field>
          <div className="grid grid-3">
            <Field label="Contact name" htmlFor="l-cname">
              <TextInput id="l-cname" value={form.contactName} disabled={!editable}
                onChange={e => setForm({ ...form, contactName: e.target.value })} />
            </Field>
            <Field label="Phone" htmlFor="l-cphone">
              <TextInput id="l-cphone" value={form.contactPhone} disabled={!editable}
                onChange={e => setForm({ ...form, contactPhone: e.target.value })} />
            </Field>
            <Field label="Email" htmlFor="l-cemail">
              <TextInput id="l-cemail" type="email" value={form.contactEmail} disabled={!editable}
                onChange={e => setForm({ ...form, contactEmail: e.target.value })} />
            </Field>
          </div>
        </Card>

        {existing.data && existing.data.enquiries.length > 0 && (
          <Card title={`Enquiries (${existing.data.enquiries.length})`} bodyClassName="card-pad-0">
            <div className="table-wrap">
              <table className="table table-tight">
                <thead>
                  <tr><th>When</th><th>Who</th><th>Contact</th><th>Message</th><th>Viewing</th></tr>
                </thead>
                <tbody>
                  {existing.data.enquiries.map(e => (
                    <tr key={e.id}>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>{shortDate(e.created_at)}</td>
                      <td>{e.name}</td>
                      <td className="muted">
                        {e.phone && <a href={`tel:${e.phone}`}>{e.phone}</a>}
                        {e.phone && e.email && <br />}
                        {e.email && <a href={`mailto:${e.email}`}>{e.email}</a>}
                      </td>
                      <td className="muted">
                        {e.stay_from && e.stay_to && (
                          <div><strong>{shortDate(e.stay_from)} to {shortDate(e.stay_to)}</strong>
                            {e.guests ? `, ${e.guests} guest${e.guests === 1 ? '' : 's'}` : ''}</div>
                        )}
                        {e.message ?? (e.stay_from ? '' : '-')}
                      </td>
                      <td>
                        {e.viewing_starts_at ? (
                          <span className="muted">
                            Booked {new Date(e.viewing_starts_at).toLocaleString('en-KE', {
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                              timeZone: 'Africa/Nairobi',
                            })}
                          </span>
                        ) : e.wants_viewing ? (
                          <div className="stack">
                            <span className="muted">
                              Wants to view{e.preferred_viewing_date ? ` · prefers ${shortDate(e.preferred_viewing_date)}` : ''}
                            </span>
                            <TextInput type="datetime-local" aria-label={`Viewing time for ${e.name}`}
                              value={viewingAt[e.id] ?? ''}
                              onChange={ev => setViewingAt(v => ({ ...v, [e.id]: ev.target.value }))} />
                            <Button size="sm" variant="secondary" loading={busy === `view-${e.id}`}
                              disabled={!viewingAt[e.id]} onClick={() => onBookViewing(e.id)}>
                              Book viewing
                            </Button>
                          </div>
                        ) : <span className="muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => navigate(paths.home)}>Cancel</Button>

          {editable && (
            <>
              <Button variant="secondary" loading={busy === 'draft'} onClick={onSaveDraft}>
                Save draft
              </Button>
              <Button loading={busy === 'publish'} onClick={onPublish}>
                {isNew ? 'Save & publish' : 'Submit for publishing'}
              </Button>
            </>
          )}

          {listing && !editable && (
            <Button variant="secondary" loading={busy === 'withdraw'} onClick={onWithdraw}>
              Withdraw
            </Button>
          )}
          {listing?.status === 'published' && (
            <>
              {/* Taken vs let are different decisions. "Let" removes the advert
                  outright; "taken" keeps it up behind a banner and is
                  reversible, for a hold that has not yet completed. */}
              {listing.is_sold_out ? (
                <Button variant="secondary" loading={busy === 'soldout'}
                  onClick={() => onSoldOut(false)}>Back to available</Button>
              ) : (
                <Button variant="secondary" loading={busy === 'soldout'}
                  onClick={() => onSoldOut(true)}>Mark as taken</Button>
              )}
              <Button variant="secondary" loading={busy === 'reserved'}
                onClick={() => onState('reserved')}>Mark reserved</Button>
              <Button variant="secondary" loading={busy === 'rented'}
                onClick={() => onState('rented')}>Mark let</Button>
              {!listing.is_sold_out && (
                <Button variant="secondary" loading={busy === 'fresh'} onClick={onStillAvailable}>
                  Still available
                </Button>
              )}
              {!listing.is_sold_out && (
                <Link className="btn btn-primary" to={paths.promote(listing.id)}>
                  Promote
                </Link>
              )}
            </>
          )}
        </div>

        {listing?.status === 'published' && (
          <p className="page-sub" style={{ textAlign: 'right' }}>
            {listing.is_sold_out && <strong>Taken · </strong>}
            Live since {shortDate(listing.published_at)} · {money(listing.rent)}/month
            {listing.expires_at && !listing.is_sold_out && (
              <> · shown until {shortDate(listing.expires_at)} unless you confirm it is still available</>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
